<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">立项评审</h1>
        <p class="text-sm text-slate-500 mt-1">对通过初审的项目进行深度内容评审和专家匹配</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">第 3 环节</span>
        <span class="px-3 py-1.5 bg-amber-50 text-amber-600 rounded-full text-xs font-semibold">进行中</span>
      </div>
    </div>

    <!-- Segmented Control -->
    <div class="flex justify-center">
      <div class="segmented-control">
        <button
          class="segmented-btn"
          :class="{ 'active': activeTab === 'content' }"
          @click="activeTab = 'content'"
        >内容评审</button>
        <button
          class="segmented-btn"
          :class="{ 'active': activeTab === 'expert' }"
          @click="activeTab = 'expert'"
        >专家遴选</button>
      </div>
    </div>

    <!-- Content Review Tab -->
    <div v-if="activeTab === 'content'" class="page-section active">
      <!-- File Upload Section -->
      <div class="glass-card rounded-3xl p-8">
        <div class="flex items-center gap-3 mb-8">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-bold text-slate-800">申报材料上传</h2>
            <p class="text-xs text-slate-500 mt-0.5">支持申报书、指南文件上传，支持单文件或批量上传</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-6 mb-8">
          <!-- Declaration Document Upload -->
          <div class="upload-zone rounded-2xl border-2 border-dashed border-slate-200 p-6 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-all cursor-pointer relative"
               @click="$refs.declarationInput.click()"
               @dragover.prevent
               @drop.prevent="handleDeclarationDrop">
            <input ref="declarationInput" type="file" multiple accept=".pdf,.doc,.docx" class="hidden" @change="handleDeclarationFiles">
            <div class="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <h3 class="text-sm font-semibold text-slate-700 mb-1">待评审申报书</h3>
            <p class="text-xs text-slate-400 mb-3">点击或拖拽文件至此处上传</p>
            <span class="text-xs text-slate-400">支持 PDF、Word 格式</span>
          </div>

          <!-- Guide Document Upload -->
          <div class="upload-zone rounded-2xl border-2 border-dashed border-slate-200 p-6 text-center hover:border-emerald-400 hover:bg-emerald-50/30 transition-all cursor-pointer relative"
               @click="$refs.guideInput.click()"
               @dragover.prevent
               @drop.prevent="handleGuideDrop">
            <input ref="guideInput" type="file" accept=".pdf,.doc,.docx" class="hidden" @change="handleGuideFile">
            <div class="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
            </div>
            <h3 class="text-sm font-semibold text-slate-700 mb-1">指南文件</h3>
            <p class="text-xs text-slate-400 mb-3">点击或拖拽文件至此处上传</p>
            <span class="text-xs text-slate-400">支持 PDF、Word 格式</span>
          </div>
        </div>

        <!-- Uploaded Files List -->
        <div v-if="uploadedDeclarations.length > 0 || guideFile" class="space-y-4 mb-8">
          <!-- Declaration Files -->
          <div v-if="uploadedDeclarations.length > 0" class="bg-slate-50 rounded-xl p-4">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-blue-500"></span>
                已上传申报书 ({{ uploadedDeclarations.length }}个)
              </h4>
              <button @click="clearDeclarationFiles" class="text-xs text-red-500 hover:text-red-600">清空</button>
            </div>
            <div class="space-y-2">
              <div v-for="(file, index) in uploadedDeclarations" :key="index" class="flex items-center justify-between bg-white rounded-lg p-3">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-slate-700">{{ file.name }}</p>
                    <p class="text-xs text-slate-400">{{ formatFileSize(file.size) }}</p>
                  </div>
                </div>
                <button @click="removeDeclarationFile(index)" class="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Guide File -->
          <div v-if="guideFile" class="bg-slate-50 rounded-xl p-4">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                已上传指南文件
              </h4>
              <button @click="clearGuideFile" class="text-xs text-red-500 hover:text-red-600">删除</button>
            </div>
            <div class="flex items-center justify-between bg-white rounded-lg p-3">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <svg class="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-medium text-slate-700">{{ guideFile.name }}</p>
                  <p class="text-xs text-slate-400">{{ formatFileSize(guideFile.size) }}</p>
                </div>
              </div>
              <button @click="clearGuideFile" class="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div class="flex justify-center">
          <button 
            class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3"
            :disabled="uploadedDeclarations.length === 0 || !guideFile || isReviewing"
            @click="startContentReview">
            <svg v-if="!isReviewing" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
            <svg v-else class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            {{ isReviewing ? '评审进行中...' : '启动内容评审' }}
          </button>
        </div>
      </div>

      <!-- Review Results -->
      <div v-if="reviewCompleted" class="glass-card rounded-3xl p-8">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <div>
              <h2 class="text-lg font-bold text-slate-800">评审结果</h2>
              <p class="text-xs text-slate-500">基于AI智能分析生成的深度评审报告</p>
            </div>
          </div>
          <button class="btn-export px-5 py-2.5 rounded-xl text-white text-sm font-medium flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
            </svg>
            导出完整报告
          </button>
        </div>

        <!-- Report Type Selection Tabs -->
        <div class="flex gap-2 mb-6 p-1 bg-slate-100 rounded-xl">
          <button 
            class="flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
            :class="activeReportType === 'single' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
            @click="activeReportType = 'single'">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            深度评估报告
            <span class="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">单项目</span>
          </button>
          <button 
            class="flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
            :class="activeReportType === 'batch' ? 'bg-white text-purple-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
            @click="activeReportType = 'batch'">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            阶梯排序榜单
            <span class="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-600">多项目</span>
          </button>
        </div>

        <!-- Single Project Report -->
        <div v-if="activeReportType === 'single'" class="space-y-6">
          <!-- Project Selector -->
          <div class="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
            <span class="text-sm font-medium text-slate-600">选择申报项目：</span>
            <select v-model="selectedProjectIndex" class="flex-1 premium-input rounded-lg px-3 py-2 text-sm">
              <option v-for="(project, idx) in singleReportData.projects" :key="idx" :value="idx">
                {{ project.name }} - {{ project.unit }}
              </option>
            </select>
          </div>

          <!-- Selected Project Overview -->
          <div class="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
            <div class="flex items-start justify-between">
              <div>
                <h3 class="text-lg font-bold text-slate-800 mb-1">{{ currentSingleProject.name }}</h3>
                <p class="text-sm text-slate-500">申报单位：{{ currentSingleProject.unit }}</p>
                <p class="text-xs text-slate-400 mt-1">评审时间：{{ currentSingleProject.reviewTime }}</p>
              </div>
              <div class="text-right">
                <div class="text-3xl font-bold text-blue-600">{{ currentSingleProject.totalScore }}</div>
                <div class="text-sm text-slate-500">综合评分</div>
              </div>
            </div>
          </div>

          <!-- Key Metrics -->
          <div class="grid grid-cols-4 gap-4">
            <div class="metric-card p-4 rounded-xl bg-white border border-slate-200">
              <div class="flex items-center gap-2 mb-2">
                <div class="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <span class="text-xs text-slate-500">推荐等级</span>
              </div>
              <div class="text-2xl font-bold text-emerald-600">{{ currentSingleProject.grade }}</div>
              <div class="text-xs text-slate-400 mt-1">{{ currentSingleProject.gradeDesc }}</div>
            </div>
            <div class="metric-card p-4 rounded-xl bg-white border border-slate-200">
              <div class="flex items-center gap-2 mb-2">
                <div class="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                </div>
                <span class="text-xs text-slate-500">修改建议</span>
              </div>
              <div class="text-2xl font-bold text-amber-600">{{ currentSingleProject.modifications }}</div>
              <div class="text-xs text-slate-400 mt-1">项待完善</div>
            </div>
            <div class="metric-card p-4 rounded-xl bg-white border border-slate-200">
              <div class="flex items-center gap-2 mb-2">
                <div class="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                  </svg>
                </div>
                <span class="text-xs text-slate-500">AI置信度</span>
              </div>
              <div class="text-2xl font-bold text-purple-600">{{ currentSingleProject.confidence }}%</div>
              <div class="text-xs text-slate-400 mt-1">多维度分析</div>
            </div>
            <div class="metric-card p-4 rounded-xl bg-white border border-slate-200">
              <div class="flex items-center gap-2 mb-2">
                <div class="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <span class="text-xs text-slate-500">评审时长</span>
              </div>
              <div class="text-2xl font-bold text-blue-600">{{ currentSingleProject.duration }}</div>
              <div class="text-xs text-slate-400 mt-1">智能分析</div>
            </div>
          </div>

          <!-- Dimension Scores Radar -->
          <div class="grid grid-cols-3 gap-6">
            <div class="col-span-1 p-5 bg-white rounded-2xl border border-slate-200">
              <h4 class="font-semibold text-slate-800 text-sm mb-4">维度评分</h4>
              <div class="space-y-3">
                <div v-for="(dim, idx) in currentSingleProject.dimensions" :key="idx" class="flex items-center gap-3">
                  <span class="text-xs text-slate-500 w-16">{{ dim.name }}</span>
                  <div class="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div class="h-full rounded-full transition-all duration-500" 
                         :style="{ width: dim.score + '%', backgroundColor: dim.color }"></div>
                  </div>
                  <span class="text-xs font-semibold w-8 text-right" :style="{ color: dim.color }">{{ dim.score }}</span>
                </div>
              </div>
            </div>
            <div class="col-span-2 space-y-4">
              <div class="p-5 bg-white rounded-2xl border border-slate-200">
                <h4 class="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2">
                  <span class="w-2 h-2 rounded-full bg-blue-500"></span>
                  项目画像
                </h4>
                <p class="text-sm text-slate-600 leading-relaxed">{{ currentSingleProject.profile }}</p>
              </div>
              <div class="p-5 bg-white rounded-2xl border border-slate-200">
                <h4 class="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2">
                  <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                  核心优势
                </h4>
                <div class="flex flex-wrap gap-2">
                  <span v-for="(adv, idx) in currentSingleProject.advantages" :key="idx" 
                        class="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs rounded-lg border border-emerald-200">
                    {{ adv }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Review Details -->
          <div class="grid grid-cols-2 gap-6">
            <div class="p-5 bg-white rounded-2xl border border-slate-200">
              <h4 class="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-amber-500"></span>
                评审意见
              </h4>
              <p class="text-sm text-slate-600 leading-relaxed">{{ currentSingleProject.opinion }}</p>
            </div>
            <div class="p-5 bg-white rounded-2xl border border-slate-200">
              <h4 class="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-red-500"></span>
                风险提示与改进建议
              </h4>
              <ul class="space-y-2">
                <li v-for="(risk, idx) in currentSingleProject.risks" :key="idx" class="flex items-start gap-2 text-sm text-slate-600">
                  <span class="text-red-500 mt-0.5">•</span>
                  {{ risk }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Batch Report -->
        <div v-else class="space-y-6">
          <!-- Batch Overview -->
          <div class="grid grid-cols-4 gap-4">
            <div class="p-5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl text-white">
              <div class="text-3xl font-bold mb-1">{{ batchReportData.totalProjects }}</div>
              <div class="text-sm text-purple-100">参评项目总数</div>
            </div>
            <div class="p-5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl text-white">
              <div class="text-3xl font-bold mb-1">{{ batchReportData.recommended }}</div>
              <div class="text-sm text-emerald-100">推荐立项</div>
            </div>
            <div class="p-5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl text-white">
              <div class="text-3xl font-bold mb-1">{{ batchReportData.conditional }}</div>
              <div class="text-sm text-amber-100">条件立项</div>
            </div>
            <div class="p-5 bg-gradient-to-br from-slate-500 to-slate-600 rounded-2xl text-white">
              <div class="text-3xl font-bold mb-1">{{ batchReportData.notRecommended }}</div>
              <div class="text-sm text-slate-100">不推荐</div>
            </div>
          </div>

          <!-- Ranking List -->
          <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div class="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h4 class="font-semibold text-slate-800">阶梯排序榜单</h4>
              <div class="flex items-center gap-4 text-xs text-slate-500">
                <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full bg-emerald-500"></span>第一梯队</span>
                <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full bg-blue-500"></span>第二梯队</span>
                <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full bg-amber-500"></span>第三梯队</span>
              </div>
            </div>
            <div class="divide-y divide-slate-100">
              <div v-for="(item, idx) in batchReportData.ranking" :key="idx" 
                   class="px-6 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4">
                <!-- Rank -->
                <div class="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm"
                     :class="{
                       'bg-emerald-100 text-emerald-600': item.tier === 1,
                       'bg-blue-100 text-blue-600': item.tier === 2,
                       'bg-amber-100 text-amber-600': item.tier === 3
                     }">
                  {{ item.rank }}
                </div>
                <!-- Info -->
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <h5 class="font-semibold text-slate-800">{{ item.name }}</h5>
                    <span class="px-2 py-0.5 text-[10px] rounded-full border"
                          :class="{
                            'bg-emerald-50 border-emerald-200 text-emerald-600': item.tier === 1,
                            'bg-blue-50 border-blue-200 text-blue-600': item.tier === 2,
                            'bg-amber-50 border-amber-200 text-amber-600': item.tier === 3
                          }">
                      {{ item.tier === 1 ? '优先推荐' : item.tier === 2 ? '建议立项' : '酌情考虑' }}
                    </span>
                  </div>
                  <p class="text-xs text-slate-500">{{ item.unit }} · 综合评分 {{ item.score }}分</p>
                </div>
                <!-- Score Bar -->
                <div class="w-32">
                  <div class="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div class="h-full rounded-full transition-all duration-500"
                         :class="{
                           'bg-emerald-500': item.tier === 1,
                           'bg-blue-500': item.tier === 2,
                           'bg-amber-500': item.tier === 3
                         }"
                         :style="{ width: item.score + '%' }"></div>
                  </div>
                </div>
                <!-- Action -->
                <button class="p-2 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Promotion Suggestions -->
          <div class="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100">
            <h4 class="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
              晋级建议
            </h4>
            <div class="grid grid-cols-3 gap-4">
              <div class="p-4 bg-white rounded-xl border border-indigo-100">
                <div class="text-xs text-slate-500 mb-1">第一梯队（优先资助）</div>
                <div class="text-lg font-bold text-emerald-600 mb-2">{{ batchReportData.suggestions.tier1.count }}项</div>
                <p class="text-xs text-slate-600">{{ batchReportData.suggestions.tier1.desc }}</p>
              </div>
              <div class="p-4 bg-white rounded-xl border border-indigo-100">
                <div class="text-xs text-slate-500 mb-1">第二梯队（建议资助）</div>
                <div class="text-lg font-bold text-blue-600 mb-2">{{ batchReportData.suggestions.tier2.count }}项</div>
                <p class="text-xs text-slate-600">{{ batchReportData.suggestions.tier2.desc }}</p>
              </div>
              <div class="p-4 bg-white rounded-xl border border-indigo-100">
                <div class="text-xs text-slate-500 mb-1">第三梯队（酌情考虑）</div>
                <div class="text-lg font-bold text-amber-600 mb-2">{{ batchReportData.suggestions.tier3.count }}项</div>
                <p class="text-xs text-slate-600">{{ batchReportData.suggestions.tier3.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Expert Selection Tab -->
    <div v-if="activeTab === 'expert'" class="page-section active">
      <!-- Expert Info Configuration -->
      <div class="glass-card rounded-3xl p-6 mb-6">
        <div class="flex items-center gap-2 mb-6">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">专家信息配置</h2>
        </div>

        <div class="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">专项计划</label>
            <select class="premium-input w-full rounded-xl p-3 text-sm">
              <option>国家重点研发计划</option>
              <option>国家自然科学基金</option>
              <option>省级科技计划</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-700 mb-2">指南方向</label>
            <select class="premium-input w-full rounded-xl p-3 text-sm">
              <option>人工智能</option>
              <option>生物医药</option>
              <option>新材料</option>
              <option>新能源</option>
            </select>
          </div>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-semibold text-slate-700 mb-2">指南名称/方向（手工填入）</label>
          <input
            type="text"
            class="premium-input w-full rounded-xl p-3 text-sm"
            placeholder="请输入指南名称或研究方向..."
          />
        </div>

        <div class="flex justify-center">
          <button class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            智能匹配专家
          </button>
        </div>
      </div>

      <!-- Three Column Layout: Project Info | Expert List | Evaluation Report -->
      <div class="grid grid-cols-12 gap-6">
        <!-- Left: Project Info Panel -->
        <div class="col-span-3">
          <div class="glass-card rounded-3xl p-5 h-full">
            <div class="mb-6">
              <h3 class="text-sm font-bold text-slate-800 mb-2">专项计划</h3>
              <p class="text-sm text-slate-600">国家自然科学基金</p>
            </div>
            
            <div class="mb-6">
              <h3 class="text-sm font-bold text-slate-800 mb-2">指南方向/名称</h3>
              <p class="text-sm text-slate-600">基于机理模型+多源数据融合的海上钻井装备预测模型研究</p>
            </div>

            <div class="mb-6">
              <h3 class="text-sm font-bold text-slate-800 mb-2">研究内容摘要</h3>
              <p class="text-xs text-slate-500 leading-relaxed">针对固井质量预测手段匮乏及样本有限问题，开展机理+数据双驱动模型研究，集成水力学、温度场等物理模型，融合钻井、地震及生产多源数据，构建智能预测模型，实现钻井井筒及井周地层基础参数预测与参数优化指导。</p>
            </div>

            <div>
              <h3 class="text-sm font-bold text-slate-800 mb-3">主要申报单位</h3>
              <div class="space-y-2">
                <div class="flex items-center gap-2">
                  <span class="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">1</span>
                  <span class="text-xs text-slate-600">清华大学</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="w-5 h-5 rounded-full bg-slate-300 text-white text-xs flex items-center justify-center font-bold">2</span>
                  <span class="text-xs text-slate-600">南开大学</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="w-5 h-5 rounded-full bg-slate-300 text-white text-xs flex items-center justify-center font-bold">3</span>
                  <span class="text-xs text-slate-600">天津科技大学</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="w-5 h-5 rounded-full bg-slate-300 text-white text-xs flex items-center justify-center font-bold">4</span>
                  <span class="text-xs text-slate-600">天津大学</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Middle: Expert List -->
        <div class="col-span-5">
          <div class="glass-card rounded-3xl p-5">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-base font-bold text-slate-800">智能遴选专家库</h2>
              <span class="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">已匹配 5 位专家</span>
            </div>

            <!-- Core Domain Experts -->
            <div class="mb-6">
              <div class="flex items-center gap-2 mb-3">
                <h3 class="text-sm font-semibold text-slate-700">核心领域专家</h3>
                <span class="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">精选 3 位</span>
              </div>
              
              <div class="space-y-3">
                <div v-for="(expert, index) in coreExperts" :key="index" 
                     class="p-4 rounded-xl border-2 cursor-pointer transition-all"
                     :class="selectedExpert?.name === expert.name ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                     @click="selectExpert(expert)">
                  <div class="flex items-start justify-between">
                    <div class="flex items-start gap-3">
                      <div class="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center text-white font-bold">
                        {{ expert.name.charAt(0) }}
                      </div>
                      <div>
                        <div class="flex items-center gap-2">
                          <h4 class="font-bold text-slate-800 text-sm">{{ expert.name }}</h4>
                          <span class="text-xs text-slate-500">{{ expert.title }} · {{ expert.institution }} · {{ expert.age }}岁</span>
                        </div>
                        <p class="text-xs text-slate-500 mt-1">研究方向：{{ expert.field }}</p>
                        <div class="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span>联系方式：{{ expert.phone }}</span>
                          <span>联系邮箱：{{ expert.email }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-lg font-bold text-red-500">{{ expert.match }}%</div>
                      <div class="text-xs text-slate-400">契合度</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Edge Domain Experts -->
            <div>
              <div class="flex items-center gap-2 mb-3">
                <h3 class="text-sm font-semibold text-slate-700">边缘领域专家</h3>
                <span class="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full">辅助参考</span>
              </div>
              
              <div class="space-y-3">
                <div v-for="(expert, index) in edgeExperts" :key="index" 
                     class="p-4 rounded-xl border-2 cursor-pointer transition-all"
                     :class="selectedExpert?.name === expert.name ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'"
                     @click="selectExpert(expert)">
                  <div class="flex items-start justify-between">
                    <div class="flex items-start gap-3">
                      <div class="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center text-white font-bold">
                        {{ expert.name.charAt(0) }}
                      </div>
                      <div>
                        <div class="flex items-center gap-2">
                          <h4 class="font-bold text-slate-800 text-sm">{{ expert.name }}</h4>
                          <span class="text-xs text-slate-500">{{ expert.title }} · {{ expert.institution }} · {{ expert.age }}岁</span>
                        </div>
                        <p class="text-xs text-slate-500 mt-1">研究方向：{{ expert.field }}</p>
                        <div class="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span>联系方式：{{ expert.phone }}</span>
                          <span>联系邮箱：{{ expert.email }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-lg font-bold text-amber-500">{{ expert.match }}%</div>
                      <div class="text-xs text-slate-400">契合度</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Evaluation Report -->
        <div class="col-span-4">
          <div class="glass-card rounded-3xl p-5 h-full">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                <h2 class="text-base font-bold text-slate-800">遴选评估详情</h2>
              </div>
              <button class="btn-export px-3 py-1.5 rounded-lg text-white text-xs font-medium flex items-center gap-1.5">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                </svg>
                导出报告
              </button>
            </div>

            <div v-if="selectedExpert" class="space-y-4">
              <!-- Expert Header -->
              <div class="flex items-center justify-between pb-4 border-b border-slate-200">
                <div>
                  <h3 class="text-lg font-bold text-slate-800">{{ selectedExpert.name }}</h3>
                  <p class="text-sm text-slate-500">评估项</p>
                </div>
                <span class="px-3 py-1 text-white text-xs rounded-full" :class="selectedExpert.type === 'core' ? 'bg-blue-500' : 'bg-amber-500'">
                  {{ selectedExpert.type === 'core' ? '核心专家' : '边缘专家' }}
                </span>
              </div>

              <!-- Evaluation Items -->
              <div class="space-y-4">
                <div v-for="(evalItem, index) in selectedExpert.evaluations" :key="index" class="flex items-start gap-3">
                  <div class="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-slate-700">{{ evalItem.title }}</h4>
                    <p class="text-xs text-slate-500 mt-1 leading-relaxed">{{ evalItem.content }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="flex flex-col items-center justify-center h-64 text-slate-400">
              <svg class="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              <p class="text-sm">点击左侧专家查看智能遴选评估报告</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Workflow Modal - Canvas Style Layout -->
    <div v-if="showWorkflowModal" class="fixed inset-0 z-[1000] flex items-center justify-center" style="padding-top: 60px;">
      <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" @click="closeWorkflowModal"></div>
      
      <!-- Main Container -->
      <div class="relative w-[95vw] max-w-7xl h-[85vh] rounded-2xl overflow-hidden border border-slate-200 bg-white flex flex-col shadow-2xl">
        
        <!-- Header -->
        <div class="flex-none h-14 flex items-center justify-between px-6 border-b border-slate-100 bg-white">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 via-indigo-500 to-violet-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-base font-semibold text-slate-800">AI智能评审工作流</h3>
              <p class="text-xs text-slate-500">多智能体协同评审引擎</p>
            </div>
          </div>
          
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-100">
              <div class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span class="text-sm font-semibold text-emerald-700">{{ workflowProgress }}%</span>
            </div>
            <button @click="closeWorkflowModal" class="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Canvas Area - Based on Reference HTML (Scaled to fit) -->
        <div class="flex-1 relative overflow-auto" style="background-color: #eaf4f9;">
          
          <!-- Scaled Container -->
          <div class="workflow-canvas-scaled" style="min-width: 100%; min-height: 100%; display: flex; align-items: flex-start; justify-content: center; padding: 20px;">
            <div class="workflow-canvas-inner" style="transform: scale(0.72); transform-origin: center top;">
          
          <!-- SVG Connections Layer -->
          <svg class="absolute inset-0 w-full h-full pointer-events-none" style="min-height: 900px; width: 1400px;">
            <defs>
              <marker id="arrowhead-gray" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#c9cdd4"/>
              </marker>
              <marker id="arrowhead-green" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#00b42a"/>
              </marker>
              <marker id="arrowhead-blue" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#1677ff"/>
              </marker>
              <marker id="arrowhead-orange" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#ff7d00"/>
              </marker>
            </defs>
            
            <!-- Connection Paths - Scaled and adjusted positions -->
            <!-- 1. 输入 -> 总控 (绿色实线) -->
            <path d="M 290 305 Q 325 305 360 305" stroke="#00b42a" stroke-width="2" fill="none" marker-end="url(#arrowhead-green)" class="connection-line"/>
            
            <!-- 2. 总控 -> 文档结构化解析 (蓝色流动线) -->
            <path d="M 480 250 Q 550 200 720 165" stroke="#1677ff" stroke-width="2" stroke-dasharray="5,3" fill="none" marker-end="url(#arrowhead-blue)" class="connection-line animate-dash"/>
            
            <!-- 3. 解析 -> 核验 (灰色实线) -->
            <path d="M 960 165 L 980 165" stroke="#c9cdd4" stroke-width="2" fill="none" marker-end="url(#arrowhead-gray)" class="connection-line"/>
            
            <!-- 4. 核验 -> 专业领域评估 (灰色实线) -->
            <path d="M 1180 250 Q 1180 285 1180 320" stroke="#c9cdd4" stroke-width="2" fill="none" marker-end="url(#arrowhead-gray)" class="connection-line"/>
            
            <!-- 5. 评估 -> 审查与反思 (灰色实线) -->
            <path d="M 1180 460 Q 1180 490 1180 520" stroke="#c9cdd4" stroke-width="2" fill="none" marker-end="url(#arrowhead-gray)" class="connection-line"/>
            
            <!-- 6. 审查与反思 -> 专业领域评估 (橙色虚线反馈) -->
            <path d="M 980 575 Q 900 450 980 380" stroke="#ff7d00" stroke-width="2" stroke-dasharray="6,4" fill="none" marker-end="url(#arrowhead-orange)" class="connection-line-dashed"/>
            
            <!-- 7. 审查与反思 -> 循环比较排序 (灰色实线) -->
            <path d="M 980 575 Q 900 575 960 600" stroke="#c9cdd4" stroke-width="2" fill="none" marker-end="url(#arrowhead-gray)" class="connection-line"/>
            
            <!-- 8. 排序 -> 返回评审总控 (灰色实线，弯曲路径) -->
            <path d="M 720 545 Q 550 545 480 400" stroke="#c9cdd4" stroke-width="2" fill="none" marker-end="url(#arrowhead-gray)" class="connection-line"/>
            
            <!-- 9. 总控 -> 过滤模版 (灰色实线) -->
            <path d="M 480 340 Q 480 430 480 520" stroke="#c9cdd4" stroke-width="2" fill="none" marker-end="url(#arrowhead-gray)" class="connection-line"/>
            
            <!-- 10. 过滤模版 -> 结果输出 (灰色实线) -->
            <path d="M 480 660 Q 480 700 480 720" stroke="#c9cdd4" stroke-width="2" fill="none" marker-end="url(#arrowhead-gray)" class="connection-line"/>
          </svg>

          <!-- Workflow Nodes Canvas -->
          <div class="relative z-10" style="width: 1400px; height: 900px; padding: 20px;">
            
            <!-- Expert Group Background -->
            <div class="absolute border-2 border-dashed rounded-2xl bg-white/40" style="left: 680px; top: 60px; width: 600px; height: 600px; border-color: #b5d8ff;">
              <div class="absolute flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full text-xs font-semibold" style="left: 20px; top: -12px; color: #1677ff;">
                <span>🛠️</span> 多领域专家 Agents 协作集群
              </div>
            </div>

            <!-- 1. 申报书、指南输入 - Left side -->
            <div class="absolute" style="left: 50px; top: 250px;">
              <div class="workflow-node-ref" :class="getNodeRefClass(workflowNodes[0].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">{{ workflowNodes[0].name }}</span>
                  <span class="status-ref" :class="workflowNodes[0].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[0].status) }}
                  </span>
                </div>
                <div class="node-desc-ref">业务前置输入<br>多模态文档解析支持</div>
                <div class="tags-ref">
                  <span class="tag-ref blue">业务输入</span>
                </div>
              </div>
            </div>

            <!-- 2. 评审总控与任务规划 - Center left -->
            <div class="absolute" style="left: 360px; top: 250px;">
              <div class="workflow-node-ref" :class="getNodeRefClass(workflowNodes[1].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">{{ workflowNodes[1].name }}</span>
                  <span class="status-ref" :class="workflowNodes[1].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[1].status) }}
                  </span>
                </div>
                <div class="node-desc-ref">解析业务意图，制定评审策略<br>任务分发与进度总控</div>
                <div class="tags-ref">
                  <span class="tag-ref orange">总指挥 Agent</span>
                  <span class="tag-ref">路由规划</span>
                </div>
              </div>
            </div>

            <!-- 3. 文档结构化解析 - Top right in cluster -->
            <div class="absolute" style="left: 720px; top: 120px;">
              <div class="workflow-node-ref" :class="getNodeRefClass(workflowNodes[2].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">{{ workflowNodes[2].name }}</span>
                  <span class="status-ref" :class="workflowNodes[2].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[2].status) }}
                  </span>
                </div>
                <div class="node-desc-ref">提取核心要素、创新点<br>规范化数据格式</div>
                <div class="tags-ref">
                  <span class="tag-ref blue">解析专家</span>
                </div>
              </div>
            </div>

            <!-- 4. 审查核验 - Top right next to 03 -->
            <div class="absolute" style="left: 980px; top: 120px;">
              <div class="workflow-node-ref" :class="getNodeRefClass(workflowNodes[3].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">{{ workflowNodes[3].name }}</span>
                  <span class="status-ref" :class="workflowNodes[3].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[3].status) }}
                  </span>
                </div>
                <div class="node-desc-ref">合规性审查、资质核验<br>查重与风控拦截</div>
                <div class="tags-ref">
                  <span class="tag-ref blue">核验专家</span>
                  <span class="tag-ref green">知识库比对</span>
                </div>
              </div>
            </div>

            <!-- 5. 专业领域评估 - Middle right -->
            <div class="absolute" style="left: 980px; top: 320px;">
              <div class="workflow-node-ref" :class="getNodeRefClass(workflowNodes[4].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">{{ workflowNodes[4].name }}</span>
                  <span class="status-ref" :class="workflowNodes[4].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[4].status) }}
                  </span>
                </div>
                <div class="node-desc-ref">技术可行性打分<br>商业价值与落地前景预测</div>
                <div class="tags-ref">
                  <span class="tag-ref blue">评估专家</span>
                </div>
              </div>
            </div>

            <!-- 6. 审查与反思 - Bottom right -->
            <div class="absolute" style="left: 980px; top: 520px;">
              <div class="workflow-node-ref" :class="getNodeRefClass(workflowNodes[5].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">{{ workflowNodes[5].name }}</span>
                  <span class="status-ref" :class="workflowNodes[5].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[5].status) }}
                  </span>
                </div>
                <div class="node-desc-ref">交叉验证评估逻辑<br>识别幻觉与逻辑漏洞</div>
                <div class="tags-ref">
                  <span class="tag-ref orange">反思机制</span>
                </div>
              </div>
            </div>

            <!-- 7. 循环比较排序 - Bottom left in cluster -->
            <div class="absolute" style="left: 720px; top: 520px;">
              <div class="workflow-node-ref" :class="getNodeRefClass(workflowNodes[6].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">{{ workflowNodes[6].name }}</span>
                  <span class="status-ref" :class="workflowNodes[6].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[6].status) }}
                  </span>
                </div>
                <div class="node-desc-ref">同类项目多维对比<br>动态调整优先级排序</div>
                <div class="tags-ref">
                  <span class="tag-ref blue">排序专家</span>
                </div>
              </div>
            </div>

            <!-- 8. 过滤模版 - Bottom center -->
            <div class="absolute" style="left: 360px; top: 520px;">
              <div class="workflow-node-ref" :class="getNodeRefClass(workflowNodes[7].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">{{ workflowNodes[7].name }}</span>
                  <span class="status-ref" :class="workflowNodes[7].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[7].status) }}
                  </span>
                </div>
                <div class="node-desc-ref">根据预设业务规则过滤<br>剔除无效/低优项目</div>
                <div class="tags-ref">
                  <span class="tag-ref">后置处理</span>
                </div>
              </div>
            </div>

            <!-- 9. 最终结果输出 - Bottom -->
            <div class="absolute" style="left: 360px; top: 720px;">
              <div class="workflow-node-ref output-wide" :class="getNodeRefClass(workflowNodes[8].status)">
                <div class="node-header-ref">
                  <span class="node-title-ref">最终结果输出</span>
                  <span class="status-ref" :class="workflowNodes[8].status">
                    <span class="status-dot-ref"></span>{{ getStatusText(workflowNodes[8].status) }}
                  </span>
                </div>
                <div class="node-desc-ref" style="background: #f7f8fa; padding: 8px; border-radius: 6px;">
                  <div class="flex items-center gap-2 text-xs text-slate-600 mb-1">
                    <span class="text-blue-500">🔹</span> 单项目：深度评估报告与画像
                  </div>
                  <div class="flex items-center gap-2 text-xs text-slate-600">
                    <span class="text-emerald-500">🔸</span> 多项目：阶梯排序榜单与晋级建议
                  </div>
                </div>
                <div class="tags-ref">
                  <span class="tag-ref green">业务输出</span>
                </div>
              </div>
            </div>

          </div>
          </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex-none h-14 flex items-center justify-between px-6 border-t border-slate-100 bg-white">
          <div class="flex items-center gap-6">
            <div class="flex items-center gap-2">
              <div class="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
              <span class="text-xs text-slate-600">已完成</span>
            </div>
            <div class="flex items-center gap-2">
              <div class="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse"></div>
              <span class="text-xs text-slate-600">执行中</span>
            </div>
            <div class="flex items-center gap-2">
              <div class="w-2.5 h-2.5 rounded-full bg-slate-300"></div>
              <span class="text-xs text-slate-600">待执行</span>
            </div>
          </div>
          
          <div class="flex items-center gap-3">
            <span class="text-xs text-slate-500">
              预计剩余 <span class="text-slate-800 font-medium">{{ remainingTime }}秒</span>
            </span>
            <button v-if="workflowCompleted" @click="closeWorkflowModal"
                    class="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all">
              查看结果
            </button>
            <button v-else @click="closeWorkflowModal"
                    class="px-4 py-2 rounded-xl bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-200 transition-all">
              后台运行
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectReview',
  data() {
    return {
      activeTab: 'content',
      activeReportType: 'single',
      reviewContent: '',
      isReviewing: false,
      reviewCompleted: false,
      uploadedDeclarations: [],
      guideFile: null,
      showWorkflowModal: false,
      workflowProgress: 0,
      workflowCompleted: false,
      remainingTime: 10,
      currentStepIndex: 0,
      workflowNodes: [
        {
          name: '申报书、指南输入',
          desc1: '支持PDF/Word格式申报书批量上传，自动提取文本内容',
          desc2: '指南文件智能解析，提取评审要点与考核指标',
          tags: ['PDF解析', 'OCR识别', '文本提取'],
          status: 'pending'
        },
        {
          name: '评审总控与任务规划',
          desc1: '基于申报书内容分析，智能分配评审任务至各Agent',
          desc2: '制定评审策略，确定评审维度与权重配置',
          tags: ['任务调度', '策略规划', 'Agent编排'],
          status: 'pending'
        },
        {
          name: '文档结构化解析',
          desc1: '提取申报书关键信息，构建项目结构化数据模型',
          desc2: '识别研究团队、研究内容、技术路线、预期成果等要素',
          tags: ['NLP解析', '实体识别', '知识图谱'],
          status: 'pending'
        },
        {
          name: '审查核验',
          desc1: '深度对比指南与任务书，检查指标一致性与完整性',
          desc2: '提取指南要素，进行形式审查与合规性校验',
          tags: ['对比分析', '要素提取', '形式审查'],
          status: 'pending'
        },
        {
          name: '专业领域评估',
          desc1: '基于领域知识库进行专业内容深度评估',
          desc2: '分析技术创新性、可行性、研究基础等核心维度',
          tags: ['领域知识', '技术评估', '创新分析'],
          status: 'pending'
        },
        {
          name: '审查与反思',
          desc1: '多维度交叉验证，识别潜在风险与问题',
          desc2: '基于评审规则进行逻辑一致性检查与反思',
          tags: ['交叉验证', '风险识别', '逻辑检查'],
          status: 'pending'
        },
        {
          name: '循环比较排序',
          desc1: '多项目间横向对比，建立相对优劣排序',
          desc2: '基于多维度评分进行阶梯式排序与分级',
          tags: ['横向对比', '排序算法', '分级评估'],
          status: 'pending'
        },
        {
          name: '过滤模版',
          desc1: '应用评审模板，过滤和优化输出内容',
          desc2: '按照规范格式生成标准化评审结果',
          tags: ['模板应用', '内容过滤', '格式标准化'],
          status: 'pending'
        },
        {
          name: '结果输出',
          desc1: '单项目：深度评估报告与画像',
          desc2: '多项目：阶梯排序榜单与晋级建议',
          tags: ['报告生成', '画像构建', '晋级建议'],
          status: 'pending'
        }
      ],
      selectedProjectIndex: 0,
      singleReportData: {
        projects: [
          {
            name: '基于机理模型+多源数据融合的海上钻井装备预测模型研究',
            unit: '清华大学',
            reviewTime: '2025-03-31 14:32:18',
            totalScore: 88,
            grade: 'A+',
            gradeDesc: '强烈推荐立项',
            modifications: 2,
            confidence: 94,
            duration: '8.5s',
            profile: '本项目针对海上钻井固井质量预测难题，创新性地提出机理模型与数据驱动相结合的混合建模方法。项目团队在该领域具有深厚的研究积累，技术路线清晰可行，预期成果具有重要的理论价值和实际应用前景。',
            advantages: ['技术创新性强', '团队实力雄厚', '应用前景广阔', '技术路线可行'],
            opinion: '项目研究目标明确，技术路线先进，创新性强。建议在实施方案中进一步明确数据获取渠道，并完善风险评估内容。预算编制合理，符合科研规律。',
            risks: [
              '海上环境数据采集难度较大，建议提前与油田单位建立数据共享机制',
              '机理模型与数据模型的融合算法复杂度较高，建议增加算法优化专项研究'
            ],
            dimensions: [
              { name: '创新性', score: 92, color: '#3b82f6' },
              { name: '可行性', score: 86, color: '#10b981' },
              { name: '研究基础', score: 90, color: '#8b5cf6' },
              { name: '团队实力', score: 88, color: '#f59e0b' },
              { name: '预期成果', score: 85, color: '#ef4444' }
            ]
          },
          {
            name: '智能钻井参数优化与实时决策系统研发',
            unit: '中国石油大学(北京)',
            reviewTime: '2025-03-31 14:33:45',
            totalScore: 85,
            grade: 'A',
            gradeDesc: '推荐立项',
            modifications: 3,
            confidence: 91,
            duration: '7.2s',
            profile: '项目聚焦智能钻井领域，开发参数优化与实时决策系统。研究内容紧密结合油田实际需求，技术方案成熟度高，具备良好的产业化前景。',
            advantages: ['产业需求明确', '技术方案成熟', '应用价值高', '团队协作良好'],
            opinion: '项目紧扣行业痛点，研究内容实用性强。建议进一步完善实时数据传输方案，并考虑与现有钻井系统的兼容性。',
            risks: [
              '实时决策系统的安全性要求极高，需加强系统安全设计',
              '现场部署环境复杂，建议增加环境适应性测试'
            ],
            dimensions: [
              { name: '创新性', score: 82, color: '#3b82f6' },
              { name: '可行性', score: 90, color: '#10b981' },
              { name: '研究基础', score: 88, color: '#8b5cf6' },
              { name: '团队实力', score: 85, color: '#f59e0b' },
              { name: '预期成果', score: 87, color: '#ef4444' }
            ]
          },
          {
            name: '深井超深井钻完井智能化关键技术研究',
            unit: '西南石油大学',
            reviewTime: '2025-03-31 14:35:12',
            totalScore: 82,
            grade: 'A-',
            gradeDesc: '建议立项',
            modifications: 4,
            confidence: 89,
            duration: '9.1s',
            profile: '项目针对深井超深井钻完井难题，开展智能化关键技术研究。研究内容具有前瞻性，但技术难度较大，需要较强的技术攻关能力。',
            advantages: ['研究前瞻性强', '技术挑战大', '战略意义重大', '团队经验丰富'],
            opinion: '项目技术难度较高，研究周期可能较长。建议细化阶段性目标，并考虑设置中期检查节点。',
            risks: [
              '深井工况复杂，模型泛化能力有待验证',
              '研究周期较长，需合理安排研究进度'
            ],
            dimensions: [
              { name: '创新性', score: 88, color: '#3b82f6' },
              { name: '可行性', score: 78, color: '#10b981' },
              { name: '研究基础', score: 85, color: '#8b5cf6' },
              { name: '团队实力', score: 82, color: '#f59e0b' },
              { name: '预期成果', score: 80, color: '#ef4444' }
            ]
          }
        ]
      },
      batchReportData: {
        totalProjects: 8,
        recommended: 3,
        conditional: 3,
        notRecommended: 2,
        ranking: [
          { rank: 1, name: '基于机理模型+多源数据融合的海上钻井装备预测模型研究', unit: '清华大学', score: 88, tier: 1 },
          { rank: 2, name: '智能钻井参数优化与实时决策系统研发', unit: '中国石油大学(北京)', score: 85, tier: 1 },
          { rank: 3, name: '深井超深井钻完井智能化关键技术研究', unit: '西南石油大学', score: 82, tier: 1 },
          { rank: 4, name: '页岩气储层智能识别与评价技术研究', unit: '成都理工大学', score: 79, tier: 2 },
          { rank: 5, name: '钻井液性能智能监测与调控系统', unit: '东北石油大学', score: 76, tier: 2 },
          { rank: 6, name: '井下复杂工况智能预警技术研究', unit: '西安石油大学', score: 74, tier: 2 },
          { rank: 7, name: '基于机器学习的钻头磨损预测模型', unit: '辽宁石油化工大学', score: 68, tier: 3 },
          { rank: 8, name: '钻井成本智能优化算法研究', unit: '常州大学', score: 65, tier: 3 }
        ],
        suggestions: {
          tier1: { count: 3, desc: '优先资助，建议全额资助，项目质量高，创新性强，可行性好' },
          tier2: { count: 3, desc: '建议资助，需根据预算情况择优支持，项目具有一定创新性和可行性' },
          tier3: { count: 2, desc: '酌情考虑，建议补充完善后再次评审，或作为储备项目' }
        }
      },
      selectedExpert: null,
      coreExperts: [
        { 
          name: '张伟', 
          title: '教授/博导', 
          institution: '中国石油大学(北京)', 
          age: 45,
          field: '油气井工程、固井水泥浆体系', 
          match: 92, 
          phone: '138****1234',
          email: 'zhangwei@cup.edu.cn',
          type: 'core',
          evaluations: [
            {
              title: '方向契合度极高',
              content: '指南重点研究"机理模型+多源数据融合"，该专家长期专注于油气井工程及固井液体系，其主导的"复杂工况下固井质量评价"项目与本指南方向高度重合，对水力学及顶替效率机理有深厚积累。'
            },
            {
              title: '研究内容相似度 >90%',
              content: '专家近期发表的关于"多物理场耦合下的固井完整性"论文，直接涵盖了指南要求的温度场计算与机理建模部分，技术路线匹配度高达90%以上。'
            },
            {
              title: '学术水平卓越',
              content: '作为博导，主持多项国家级重点研发计划，在SPE Journal等顶级期刊发表论文20余篇，具备极强的理论指导实践能力。'
            },
            {
              title: '合规性审查通过',
              content: '经背景调查，该专家政治立场坚定，无任何科研诚信不良记录，且与本次申报单位无利益关联，符合回避原则。'
            }
          ]
        },
        { 
          name: '李强', 
          title: '副教授', 
          institution: '西南石油大学', 
          age: 38,
          field: '多相流数值模拟、井下温度场', 
          match: 90, 
          phone: '139****5678',
          email: 'liqiang@swpu.edu.cn',
          type: 'core',
          evaluations: [
            {
              title: '关键技术与指南互补',
              content: '指南需解决"训练样本有限"难题，该专家擅长的多相流数值模拟与井下温度场研究，能为生成高保真机理数据提供核心算法支持，有效补充实测数据不足。'
            },
            {
              title: '研究内容相似度 >85%',
              content: '其构建的"井下多相流动仿真模型"可直接迁移至本项目的固井过程多物理场计算模块，预计可减少30%的模型开发周期。'
            },
            {
              title: '行业影响力大',
              content: '在西南地区油气田现场应用经验丰富，能够将理论模型与实际钻井数据有效融合，确保预测模型的落地性。'
            },
            {
              title: '合规性审查通过',
              content: '无政治作风问题，无学术不端记录，符合评审专家回避制度要求。'
            }
          ]
        },
        { 
          name: '王芳', 
          title: '教授', 
          institution: '清华大学', 
          age: 42,
          field: '工业大数据融合、机器学习建模', 
          match: 89, 
          phone: '137****9012',
          email: 'wangfang@tsinghua.edu.cn',
          type: 'core',
          evaluations: [
            {
              title: '数据融合能力突出',
              content: '指南核心难点在于"机理+数据双向驱动"，该专家来自清华大学，主攻工业大数据融合与机器学习建模，正是解决本指南中多源异构数据（钻井、地质、固井）融合的关键人选。'
            },
            {
              title: '研究内容相似度 >88%',
              content: '其团队开发的"小样本条件下的智能预测框架"与本指南提出的"构建智能固井质量预测模型"目标完全一致，技术契合度极高。'
            },
            {
              title: '跨学科创新优势',
              content: '具备深厚的数学与计算机背景，能从全新视角优化固井质量与地质因素的映射关系，为参数优化提供实时算法指导。'
            },
            {
              title: '合规性审查通过',
              content: '学术声誉良好，无违规记录，符合非回避专家标准。'
            }
          ]
        }
      ],
      edgeExperts: [
        { 
          name: '吴刚', 
          title: '教授', 
          institution: '哈尔滨工业大学', 
          age: 50,
          field: '特种水泥材料微观结构', 
          match: 65, 
          phone: '133****6789',
          email: 'wugang@hit.edu.cn',
          type: 'edge',
          evaluations: [
            {
              title: '特定领域补充',
              content: '虽然其主要研究水泥材料微观结构属于边缘领域（契合度55%），但固井质量的根本在于材料性能。引入该专家可从微观机理层面解释宏观预测模型的偏差，提供独特的材料学视角。'
            },
            {
              title: '学术水平高',
              content: '在特种水泥领域具有权威性，可协助验证预测模型中材料参数设定的合理性。'
            },
            {
              title: '合规性审查通过',
              content: '无政治及诚信问题，符合非回避原则。'
            }
          ]
        },
        { 
          name: '郑华', 
          title: '副教授', 
          institution: '北京大学', 
          age: 35,
          field: '数据挖掘与知识发现', 
          match: 58, 
          phone: '136****3456',
          email: 'zhenghua@pku.edu.cn',
          type: 'edge',
          evaluations: [
            {
              title: '算法底层支持',
              content: '其研究的通用人工智能算法虽未直接针对固井（契合度58%），但在模型收敛速度和泛化能力优化上具有通用价值，可为本项目提供底层算法优化建议。'
            },
            {
              title: '跨领域创新潜力',
              content: '作为年轻学者，思维活跃，有助于打破传统石油工程思维的局限，引入前沿AI技术。'
            },
            {
              title: '合规性审查通过',
              content: '背景清晰，无不良记录，符合评审要求。'
            }
          ]
        }
      ]
    }
  },
  computed: {
    currentSingleProject() {
      return this.singleReportData.projects[this.selectedProjectIndex] || this.singleReportData.projects[0]
    }
  },
  mounted() {
    const tab = this.$route.query.tab
    if (tab === 'expert') {
      this.activeTab = 'expert'
    } else if (tab === 'content') {
      this.activeTab = 'content'
    }
  },
  watch: {
    '$route.query.tab'(newTab) {
      if (newTab === 'expert') {
        this.activeTab = 'expert'
      } else if (newTab === 'content') {
        this.activeTab = 'content'
      }
    }
  },
  methods: {
    selectExpert(expert) {
      this.selectedExpert = expert
    },
    handleDeclarationFiles(event) {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        if (!this.uploadedDeclarations.find(f => f.name === file.name)) {
          this.uploadedDeclarations.push(file)
        }
      })
    },
    handleDeclarationDrop(event) {
      const files = Array.from(event.dataTransfer.files)
      files.forEach(file => {
        if (!this.uploadedDeclarations.find(f => f.name === file.name)) {
          this.uploadedDeclarations.push(file)
        }
      })
    },
    handleGuideFile(event) {
      const file = event.target.files[0]
      if (file) {
        this.guideFile = file
      }
    },
    handleGuideDrop(event) {
      const file = event.dataTransfer.files[0]
      if (file) {
        this.guideFile = file
      }
    },
    removeDeclarationFile(index) {
      this.uploadedDeclarations.splice(index, 1)
    },
    clearDeclarationFiles() {
      this.uploadedDeclarations = []
    },
    clearGuideFile() {
      this.guideFile = null
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    startContentReview() {
      this.showWorkflowModal = true
      this.workflowProgress = 0
      this.workflowCompleted = false
      this.remainingTime = 10
      this.currentStepIndex = 0
      
      // Reset all nodes
      this.workflowNodes.forEach(node => node.status = 'pending')
      
      this.runWorkflow()
    },
    runWorkflow() {
      const totalNodes = this.workflowNodes.length
      const nodeDuration = 10000 / totalNodes

      const interval = setInterval(() => {
        if (this.currentStepIndex < totalNodes) {
          // Mark previous node as completed
          if (this.currentStepIndex > 0) {
            this.workflowNodes[this.currentStepIndex - 1].status = 'completed'
          }
          // Mark current node as running
          this.workflowNodes[this.currentStepIndex].status = 'running'
          
          // Update progress
          this.workflowProgress = Math.round(((this.currentStepIndex + 1) / totalNodes) * 100)
          this.remainingTime = Math.max(0, 10 - Math.round((this.currentStepIndex + 1) * (10 / totalNodes)))
          
          this.currentStepIndex++
        } else {
          // All nodes completed
          this.workflowNodes[totalNodes - 1].status = 'completed'
          this.workflowProgress = 100
          this.workflowCompleted = true
          this.remainingTime = 0
          this.reviewCompleted = true
          clearInterval(interval)
        }
      }, nodeDuration)
    },
    closeWorkflowModal() {
      this.showWorkflowModal = false
    },
    getNodeBorderClass(status) {
      switch (status) {
        case 'completed':
          return 'border-emerald-400 shadow-lg shadow-emerald-100'
        case 'running':
          return 'border-blue-400 shadow-lg shadow-blue-100 ring-2 ring-blue-100'
        default:
          return 'border-slate-200'
      }
    },
    getStatusDotClass(status) {
      switch (status) {
        case 'completed':
          return 'bg-emerald-500'
        case 'running':
          return 'bg-blue-500 animate-pulse'
        default:
          return 'bg-slate-300'
      }
    },
    // New V2 helper methods
    getNodeV2Class(status) {
      return `node-${status}`
    },
    getStatusDotClassV2(status) {
      return `status-dot-${status}`
    },
    getStatusTextClass(status) {
      return `status-text-${status}`
    },
    getStatusText(status) {
      switch (status) {
        case 'completed':
          return '已完成'
        case 'running':
          return '进行中'
        default:
          return '等待中'
      }
    },
    // Reference HTML helper methods
    getNodeRefClass(status) {
      return status
    }
  }
}
</script>

<style scoped>
.segmented-control {
  display: flex;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  padding: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.segmented-btn {
  padding: 10px 32px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  transition: all 0.3s ease;
  border: none;
  background: transparent;
  cursor: pointer;
}

.segmented-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.glass-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
}

.premium-input {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s ease;
}

.premium-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background: white;
}

.btn-action {
  background: linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-export {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  transition: all 0.3s ease;
}

.btn-export:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
}

.upload-zone {
  transition: all 0.3s ease;
}

.upload-zone:hover {
  transform: translateY(-2px);
}

.metric-card {
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.review-section {
  transition: all 0.3s ease;
}

.review-section:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.number-font {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'tnum';
}

/* Workflow Canvas Node Styles */
.workflow-node-canvas {
  width: 180px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.workflow-node-canvas:hover {
  transform: translateY(-4px);
}

.node-badge {
  position: absolute;
  top: -8px;
  left: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  z-index: 10;
}

.node-badge.input {
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  color: white;
}

.node-badge.control {
  background: linear-gradient(135deg, #8b5cf6, #a855f7);
  color: white;
}

.node-badge.agent {
  background: linear-gradient(135deg, #f59e0b, #f97316);
  color: white;
}

.node-badge.process {
  background: linear-gradient(135deg, #10b981, #34d399);
  color: white;
}

.node-badge.output {
  background: linear-gradient(135deg, #ec4899, #f43f5e);
  color: white;
}

.node-content {
  background: white;
  border-radius: 12px;
  padding: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 2px solid;
  transition: all 0.3s ease;
}

.node-active .node-content {
  border-color: #3b82f6;
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
}

.node-done .node-content {
  border-color: #10b981;
  box-shadow: 0 0 15px rgba(16, 185, 129, 0.2);
}

/* Glow Animation */
@keyframes glow {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-glow {
  animation: glow 2s ease-in-out infinite;
}

/* Flow Right Animation */
@keyframes flowRight {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-flow-right {
  animation: flowRight 1.2s linear infinite;
}

/* Flow Down Animation */
@keyframes flowDown {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}

.animate-flow-down {
  animation: flowDown 1.2s linear infinite;
}

/* Line Clamp */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Progress Animation */
@keyframes progress {
  0% {
    width: 0%;
    left: 0;
  }
  50% {
    width: 50%;
    left: 25%;
  }
  100% {
    width: 0%;
    left: 100%;
  }
}

.animate-progress {
  animation: progress 2s ease-in-out infinite;
  position: relative;
}

/* Bounce Animation */
@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.animate-bounce {
  animation: bounce 0.6s infinite;
}

/* Pulse Animation */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Spin Animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Dash Animation for feedback lines */
@keyframes dash {
  to {
    stroke-dashoffset: -24;
  }
}

.animate-dash {
  animation: dash 1s linear infinite;
}

/* Connection line hover effects */
.connection-line {
  transition: all 0.3s ease;
}

.connection-line-dashed {
  opacity: 0.8;
}

/* Organic flow animation */
@keyframes flowOrganic {
  0% {
    stroke-dashoffset: 100;
  }
  100% {
    stroke-dashoffset: 0;
  }
}

.animate-flow-organic {
  animation: flowOrganic 2s ease-in-out infinite;
}

/* New Workflow Node V2 Styles */
.workflow-node-v2 {
  width: 200px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
}

.workflow-node-v2:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transform: translateY(-2px);
}

/* Status-based styling */
.workflow-node-v2.node-completed {
  border-color: #10b981;
  border-top: 3px solid #10b981;
}

.workflow-node-v2.node-running {
  border-color: #3b82f6;
  border-top: 3px solid #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.workflow-node-v2.node-pending {
  border-color: #e2e8f0;
  opacity: 0.8;
}

.workflow-node-v2.output-node.node-completed {
  border-color: #10b981;
  background: linear-gradient(to bottom right, #f0fdf4, white);
}

/* Status dots */
.status-dot-completed {
  background: #10b981;
}

.status-dot-running {
  background: #3b82f6;
  animation: pulse 2s infinite;
}

.status-dot-pending {
  background: #cbd5e1;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Status text colors */
.status-text-completed {
  color: #10b981;
}

.status-text-running {
  color: #3b82f6;
}

.status-text-pending {
  color: #94a3b8;
}

/* Reference HTML Node Styles */
.workflow-node-ref {
  position: absolute;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  width: 240px;
  box-sizing: border-box;
  border: 1px solid #e5e6eb;
  z-index: 10;
  transition: transform 0.2s, box-shadow 0.2s;
}

.workflow-node-ref:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
}

.workflow-node-ref.output-wide {
  width: 300px;
}

/* Top border status */
.workflow-node-ref.completed {
  border-top: 4px solid #00b42a;
}

.workflow-node-ref.running {
  border-top: 4px solid #1677ff;
}

.workflow-node-ref.pending {
  border-top: 4px solid #c9cdd4;
}

/* Node header */
.node-header-ref {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.node-title-ref {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}

/* Status indicator */
.status-ref {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #86909c;
}

.status-ref.completed {
  color: #00b42a;
}

.status-ref.running {
  color: #1677ff;
}

.status-dot-ref {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 6px;
  background: #c9cdd4;
}

.status-ref.completed .status-dot-ref {
  background: #00b42a;
}

.status-ref.running .status-dot-ref {
  background: #1677ff;
  animation: pulse-ref 2s infinite;
}

@keyframes pulse-ref {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Node description */
.node-desc-ref {
  font-size: 13px;
  color: #86909c;
  line-height: 1.6;
  margin-bottom: 12px;
}

/* Tags */
.tags-ref {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-ref {
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 4px;
  background-color: #f2f3f5;
  color: #4e5969;
  border: 1px solid #e5e6eb;
}

.tag-ref.blue {
  background-color: #e8f3ff;
  color: #1677ff;
  border-color: #b5d8ff;
}

.tag-ref.green {
  background-color: #e8ffea;
  color: #00b42a;
  border-color: #b7ebb4;
}

.tag-ref.orange {
  background-color: #fff7e8;
  color: #ff7d00;
  border-color: #ffe4ba;
}
</style>
